import type { RefObject, ChangeEvent, FC, MouseEvent } from "react";
import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import { useTranslation } from "@litres-libs/i18n";
import { ClearIcon } from "../../icons";
import styles from "./SimpleSearch.module.scss";

enum EPlaceholderText {
  small = "Книга, серия, автор",
  normal = "Книга, серия, автор, жанр, тег",
  large = "Книга, серия, автор, жанр, тег, издательство",
}

interface ISimpleSearchProps {
  onChange?: (value: string) => void;
  onSubmit?: (event: MouseEvent<HTMLFormElement>) => void;
  onFocus?: () => void;
  onClick?: () => void;
  handleClear?: () => void;
  inputRef?: RefObject<HTMLInputElement>;
  searchValue?: string;
  placeholder?: string;
}

export const SimpleSearch: FC<ISimpleSearchProps> = ({
  onChange,
  onSubmit,
  onFocus,
  onClick,
  handleClear,
  inputRef,
  searchValue = "",
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [hasError, setHasError] = useState(false);
  const [placeholderText, setPlaceholderText] = useState(placeholder || "");
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputText = inputValue || searchValue;

  const updatePlaceholderText = (): void => {
    if (!placeholder && wrapperRef.current) {
      const width = wrapperRef.current.clientWidth;

      switch (true) {
        case width < 300: {
          setPlaceholderText(EPlaceholderText.small);
          break;
        }
        case width >= 300 && width <= 400: {
          setPlaceholderText(EPlaceholderText.normal);
          break;
        }
        case width > 400: {
          setPlaceholderText(EPlaceholderText.large);
          break;
        }
      }
    }
  };

  useLayoutEffect(() => {
    if (wrapperRef.current?.clientWidth) {
      updatePlaceholderText();
    }
  }, [wrapperRef.current?.clientWidth]);

  const changeInputValue = ({
    target,
  }: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(target.value);
  };

  const handleClearClick = () => {
    setInputValue("");
    handleClear?.();
  };

  const handleSubmit = (event: MouseEvent<HTMLFormElement>): void => {
    if (onSubmit) {
      onSubmit(event);
    } else {
      handleRedirect(event);
    }
  };

  const handleRedirect = (event: MouseEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (inputValue.length >= 3) {
      const encodedInputValue = encodeURIComponent(inputValue);
      window.location.href = `/search/?q=${encodedInputValue}`;
    } else {
      setHasError(true);
    }
  };

  useEffect(() => {
    if (hasError) {
      setHasError(false);
    }

    onChange?.(inputValue);
  }, [inputValue]);

  return (
    <form
      action="/search/"
      method="get"
      autoComplete="off"
      onSubmit={handleSubmit}
      data-testid="simple-search__form"
    >
      <div ref={wrapperRef} className={styles.wrapper}>
        <div className={styles.inputContainer}>
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            placeholder={placeholderText && t(placeholderText)}
            maxLength={150}
            onChange={changeInputValue}
            value={inputText}
            autoComplete="off"
            onFocus={onFocus}
            onClick={onClick}
            data-testid="search__input"
          />

          {hasError && (
            <span className={styles.error}>
              {t("Пожалуйста, введите три или более символа")}
            </span>
          )}

          {inputText.length > 0 && (
            <button
              type="button"
              onClick={handleClearClick}
              className={styles.clearButton}
              data-testid="deleteTextIcon--mobile"
            >
              <ClearIcon />
            </button>
          )}

          <button
            className={styles.search_button}
            type="submit"
            data-testid="search__button"
          >
            {t("Найти")}
          </button>
        </div>
      </div>
    </form>
  );
};
